import {
  Agent as HttpAgent,
  ClientRequest,
  ClientRequestArgs,
  IncomingMessage,
  request as httpRequest
} from "http";
import { Agent as HttpsAgent, request as httpsRequest } from "https";
import { parse as parseUrl, Url } from "url";
import { btoa } from "./btoa";
import { joinPath } from "./joinPath";
import { Errback } from "./types";

export type ArangojsResponse = IncomingMessage & {
  request: ClientRequest;
  body?: any;
  host?: number;
};

export type ArangojsError = Error & {
  request: ClientRequest;
  code: string;
};

export interface RequestOptions {
  method: string;
  url: Url;
  headers: { [key: string]: string };
  body: any;
  expectBinary: boolean;
  timeout?: number;
}

export interface RequestFunction {
  (opts: RequestOptions, cb: Errback<ArangojsResponse>): void;
  close?: () => void;
}

export const isBrowser = false;

export function createRequest(
  baseUrl: string,
  agentOptions: any,
  agent: any
): RequestFunction {
  const baseUrlParts = parseUrl(baseUrl);
  if (!baseUrlParts.protocol) {
    throw new Error(`Invalid URL (no protocol): ${baseUrl}`);
  }
  const isTls = baseUrlParts.protocol === "https:";
  let socketPath: string | undefined;
  if (baseUrl.startsWith(`${baseUrlParts.protocol}//unix:`)) {
    if (!baseUrlParts.pathname) {
      throw new Error(
        `Unix socket URL must be in the format http://unix:/socket/path, http+unix:///socket/path or unix:///socket/path not ${baseUrl}`
      );
    }
    const i = baseUrlParts.pathname.indexOf(":");
    if (i === -1) {
      socketPath = baseUrlParts.pathname;
      baseUrlParts.pathname = undefined;
    } else {
      socketPath = baseUrlParts.pathname.slice(0, i);
      baseUrlParts.pathname = baseUrlParts.pathname.slice(i + 1) || undefined;
    }
  }
  if (socketPath && !socketPath.replace(/\//g, "").length) {
    throw new Error(`Invalid URL (empty unix socket path): ${baseUrl}`);
  }
  if (!agent) {
    if (isTls) agent = new HttpsAgent(agentOptions);
    else agent = new HttpAgent(agentOptions);
  }
  return Object.assign(
    function request(
      { method, url, headers, body, timeout }: RequestOptions,
      callback: Errback<ArangojsResponse>
    ) {
      let path = baseUrlParts.pathname
        ? url.pathname
          ? joinPath(baseUrlParts.pathname, url.pathname)
          : baseUrlParts.pathname
        : url.pathname;
      const search = url.search
        ? baseUrlParts.search
          ? `${baseUrlParts.search}&${url.search.slice(1)}`
          : url.search
        : baseUrlParts.search;
      if (search) path += search;
      if (body && !headers["content-length"]) {
        headers["content-length"] = String(Buffer.byteLength(body));
      }
      if (!headers["authorization"]) {
        headers["authorization"] = `Basic ${btoa(
          baseUrlParts.auth || "root:"
        )}`;
      }
      const options: ClientRequestArgs = { path, method, headers, agent };
      if (socketPath) {
        options.socketPath = socketPath;
      } else {
        options.host = baseUrlParts.hostname;
        options.port = baseUrlParts.port;
      }
      let called = false;
      let timerForTimeout: any;
      try {
        const req = (isTls ? httpsRequest : httpRequest)(
          options,
          (res: IncomingMessage) => {
            const data: Buffer[] = [];
            res.on("data", chunk => data.push(chunk as Buffer));
            res.on("end", () => {
              clearTimeout(timerForTimeout);
              const result = res as ArangojsResponse;
              result.request = req;
              result.body = Buffer.concat(data);
              if (called) return;
              called = true;
              callback(null, result);
            });
          }
        );
        if (timeout) {
          req.setTimeout(timeout);
        }
        req.on("timeout", () => {
          req.abort();
        });

        if (agentOptions.timeout) {
          timerForTimeout = setTimeout(() => {
            timerForTimeout = null;
            req.abort();
            const err = new Error("Arango Query Timeout") as ArangojsError;
            err.code = "ARANGO_QUERY_TIMEOUT";
            callback(err);
          }, agentOptions.timeout);
        }

        req.on("error", err => {
          clearTimeout(timerForTimeout);
          const error = err as ArangojsError;
          error.request = req;
          if (called) return;
          called = true;
          callback(err);
        });
        if (body) req.write(body);
        req.end();
      } catch (e) {
        if (called) return;
        called = true;
        callback(e);
      }
    },
    {
      close() {
        agent.destroy();
      }
    }
  );
}
